import { useId } from "react";

/**
 * Credablr brand logo — rounded-square emblem with a gold "C" monogram +
 * "CREDABLR" serif wordmark + tagline. Gold + dark-green identity.
 * tone: "onLight" (dark text) | "onDark" (light text)
 */
const SIZES = {
  sm: { box: 34, word: "text-lg", tag: "text-[0.48rem]", gap: "gap-2", tagMt: "mt-0.5" },
  md: { box: 44, word: "text-2xl", tag: "text-[0.5rem]", gap: "gap-2.5", tagMt: "mt-1" },
  lg: { box: 62, word: "text-4xl", tag: "text-[0.62rem]", gap: "gap-3.5", tagMt: "mt-1.5" },
};

export const Emblem = ({ size = 42, className = "" }) => {
  const gid = useId().replace(/:/g, "");
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`petal-${gid}`} x1="6" y1="5" x2="42" y2="43" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#F2E0A6" />
          <stop offset="0.5" stopColor="#C2984A" />
          <stop offset="1" stopColor="#8A6526" />
        </linearGradient>
      </defs>
      {/* Dark rounded-square chip */}
      <rect
        x="1.5"
        y="1.5"
        width="45"
        height="45"
        rx="12"
        fill="#0C1C18"
        stroke="rgba(194,152,74,0.35)"
        strokeWidth="1"
      />
      {/* Four-petal gold motif (leaves pointing to the four corners) */}
      <g fill={`url(#petal-${gid})`}>
        <path d="M24 24 Q39.21 25.77 37.43 10.57 Q22.23 8.79 24 24 Z" />
        <path d="M24 24 Q22.23 39.21 37.43 37.43 Q39.21 22.23 24 24 Z" />
        <path d="M24 24 Q8.79 22.23 10.57 37.43 Q25.77 39.21 24 24 Z" />
        <path d="M24 24 Q25.77 8.79 10.57 10.57 Q8.79 25.77 24 24 Z" />
      </g>
    </svg>
  );
};

export const Logo = ({ tone = "onLight", size = "md", showTagline = true, className = "" }) => {
  const s = SIZES[size] || SIZES.md;
  const onDark = tone === "onDark";

  const wordColor = onDark ? "text-paper" : "text-ink";
  const tagColor = onDark ? "text-paper/55" : "text-ink/45";

  return (
    <span className={`inline-flex items-center ${s.gap} ${className}`} aria-label="Credablr">
      <Emblem size={s.box} className="shrink-0" />
      <span className="flex flex-col justify-center leading-none">
        <span className={`font-display font-semibold uppercase tracking-[0.12em] ${s.word} ${wordColor}`}>
          Credablr
        </span>
        {showTagline && (
          <span className={`font-mono-cap ${s.tag} ${tagColor} ${s.tagMt} tracking-[0.16em]`}>
            Credit Enabler For Your Business
          </span>
        )}
      </span>
    </span>
  );
};
